import { useEffect, useState } from 'react';
import type { UserProfile } from '../../../../../shared/types';
import { getCompanyUsers, updateManagerStaff } from '../../../services/user.service';
import { UserTable } from '../../common/UserTable';
import { BaseModal } from '../../common/BaseModal';
import { ModalButtonGroup } from '../../common/ModalButtonGroup';
import { ErrorMessage } from '../../ErrorMessage';

interface AssignManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  staffMember: UserProfile;
  companyId: string;
  currentManager?: UserProfile;
  onSuccess: () => void;
}

export function AssignManagerModal({ isOpen, onClose, staffMember, companyId, currentManager, onSuccess }: AssignManagerModalProps) {
  const [managers, setManagers] = useState<UserProfile[]>([]);
  const [selectedManagerId, setSelectedManagerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedManagerId(currentManager?.uid ?? null);
      setError(null);
      loadManagers();
    }
  }, [isOpen, currentManager?.uid]);

  const loadManagers = async () => {
    setLoading(true);
    try {
      const users = await getCompanyUsers(companyId, false);
      setManagers(users.filter(u => u.userType === 'manager'));
    } catch {
      setError('Failed to load managers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedManagerId) return;

    setSubmitting(true);
    setError(null);
    try {
      const newManager = managers.find(m => m.uid === selectedManagerId)!;

      // Remove from old manager first if reassigning
      if (currentManager && currentManager.uid !== selectedManagerId) {
        const oldStaffIds = (currentManager.managedStaffIds || []).filter(id => id !== staffMember.uid);
        await updateManagerStaff(currentManager.uid, companyId, oldStaffIds);
      }

      // Add to new manager (if not already there)
      const newStaffIds = newManager.managedStaffIds || [];
      if (!newStaffIds.includes(staffMember.uid)) {
        await updateManagerStaff(newManager.uid, companyId, [...newStaffIds, staffMember.uid]);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign manager. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={currentManager ? 'Edit Manager Assignment' : 'Assign Manager'}
      size="medium"
      closeOnEscape={!submitting}
      closeOnBackdropClick={!submitting}
      loading={loading}
    >
      <div style={{ marginBottom: '24px' }}>
        <UserTable
          rows={[
            { label: 'Staff Member', value: `${staffMember.firstName} ${staffMember.lastName}` },
            { label: 'Email', value: staffMember.email },
            ...(currentManager ? [{ label: 'Current Manager', value: `${currentManager.firstName} ${currentManager.lastName}` }] : []),
          ]}
          columns={[
            { render: (row) => <span style={{ color: '#6b7280' }}>{row.label}</span>, width: '1%', whiteSpace: 'nowrap' },
            { render: (row) => <span style={{ color: '#111827', fontWeight: '500', wordBreak: 'break-all' }}>{row.value}</span> },
          ]}
        />
      </div>

      {error && (
        <div style={{ marginBottom: '16px' }}>
          <ErrorMessage message={error} onDismiss={() => setError(null)} />
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Select Manager</span>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
              Loading managers...
            </div>
          ) : managers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
              No managers available. Invite a manager first.
            </div>
          ) : (
            <div style={{ border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden' }}>
              {managers.map((manager) => (
                <label
                  key={manager.uid}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    backgroundColor: selectedManagerId === manager.uid ? '#eff6ff' : 'transparent',
                  }}
                >
                  <input
                    type="radio"
                    name="manager"
                    value={manager.uid}
                    checked={selectedManagerId === manager.uid}
                    onChange={() => setSelectedManagerId(manager.uid)}
                    disabled={submitting}
                    style={{ marginRight: '12px', width: '18px', height: '18px', cursor: submitting ? 'not-allowed' : 'pointer' }}
                  />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                      {manager.firstName} {manager.lastName}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {manager.email} · {(manager.managedStaffIds?.length || 0)} staff assigned
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <ModalButtonGroup
          onCancel={onClose}
          submitType="submit"
          submitLabel={currentManager ? 'Save Changes' : 'Assign Manager'}
          isLoading={submitting}
          loadingLabel="Saving..."
          disabled={loading || !selectedManagerId || selectedManagerId === currentManager?.uid}
        />
      </form>
    </BaseModal>
  );
}
